import { FC } from 'react';
import styled from 'styled-components';
import ActivityCard from './ActivityCard';
import Dialog from '../Dialog';
import Restorative, { ActivitySummary } from '../../services/Restorative';
import { ReactComponent as CloseIcon } from '../../assets/icons/x.svg';
import { ReactComponent as WarningCircleIcon } from '../../assets/icons/warning-circle.svg';

const Container = styled(Dialog)(({ theme }) => {
  const { color } = theme.new;

  return `
    padding: 1.5rem 3rem 1.5rem 3rem;
    border-radius: 20px;
    background-color: ${color.shade[1]};
    z-index: 11;
    position: fixed;
    left: 50%;
    top: 50%;
    min-height: 300px;
    transform: translate(-50%, -50%);
  `;
});

const Header = styled.div`
  display: flex;
  flex-flow: row nowrap;
  justify-content: flex-start;
  align-items: center;
  margin-bottom: 1.88rem;
  gap: 0.875rem;
`;

const HeaderTitle = styled.h2(({ theme }) => {
  const { type, color } = theme.new;

  return `
    font-size: ${type.size[32]};
    color: ${color.shade[3]};
    line-height: 1.2em;
    font-weight: ${type.weight.bold};
    flex-shrink: 1;
  `;
});

const Date = styled.h2(({ theme }) => {
  const { type, color } = theme.new;

  return `
    font-size: ${type.size[24]};
    color: ${color.neutral[8]};
    line-height: 1.2em;
    font-weight: ${type.weight.medium};
    flex-grow: 1;
  `;
});

const CloseButton = styled.button(({ theme }) => {
  const { color } = theme.new;

  return `
    background-color: ${color.shade[1]};
    display: flex;
    width: 2.875rem;
    height: 2.875rem;
    justify-content: center;
    align-items: center;
    box-shadow: 0px 1px 2px 0px rgba(16, 24, 40, 0.05);
    border-radius: 100px;

    &:hover {
      box-shadow: 0px 1px 3px rgba(16, 24, 40, 0.7);
    }

    svg {
      height: 1.25rem;
      width: 1.25rem;

      path {
        stroke: ${color.shade[2]};
      }
    }
  `;
});

const Body = styled.div`
  display: flex;
  flex-flow: row wrap;
  justify-content: flex-start;
  gap: 0.5rem;
`;

const BodyTitle = styled.h3(({ theme }) => {
  const { color, type } = theme.new;

  return `
    width: 100%;
    font-size: ${type.size[12]};
    font-weight: ${type.weight.bold};
    color: ${color.neutral[8]};
    letter-spacing: 0.015rem;
  `;
});

const Warning = styled.section(({ theme }) => {
  const { color, type } = theme.new;

  return `
    max-width: 37.625rem;
    padding-top: 1.13rem;
    color: ${color.error[2]};
    font-family: ${type.face};
    font-size: ${type.size[14]};
    font-weight: ${type.weight.regular};
    line-height: normal;
    letter-spacing: 0.00219rem;
    display: flex;
    flex-flow: row nowrap;
    justify-content: flex-start;
    gap: 0.38rem;
  `;
});

const WarningIcon = styled(WarningCircleIcon)(({ theme }) => {
  return `
    height: 1.125rem;
    width: 1.125rem;

    path: {
      stroke: ${theme.new.color.error[2]};
    }
  `;
});

export const getDateNumberSuffix = (day: number) => {
  if (day >= 11 && day <= 13) {
    return 'th';
  }

  const lastDigit = day % 10;

  switch (lastDigit) {
    case 1:
      return 'st';
    case 2:
      return 'nd';
    case 3:
      return 'rd';
    default:
      return 'th';
  }
};

export const formatDate = (date: Date) => {
  const formattedDate = date.toLocaleDateString('en-US', {
    weekday: 'long',
    month: 'long',
    day: 'numeric',
    year: 'numeric',
  });
  const day = date.getDate();
  const suffix = getDateNumberSuffix(day);

  return formattedDate.replace(/\d+/, (match: string) => match + suffix);
};

interface RestpratoveActivitySummaryProps extends ActivitySummary {
  isOpen: boolean;
  closeCallback: () => void;
}

const RestorativeActivitySummary: FC<RestpratoveActivitySummaryProps> = ({
  title,
  date,
  programs,
  name,
  closeCallback,
  isOpen,
}) => {
  const programLoop = programs.map((program) => {
    return <ActivityCard {...program} />;
  });

  const showWarning = Restorative.getProgramConflicts(programs);

  return (
    <Container isOpen={isOpen} canClose={false} closeCallback={closeCallback} noStyles noPadding>
      <Header>
        <HeaderTitle>{title}</HeaderTitle>

        <Date>{formatDate(date)}</Date>

        <CloseButton title="Close" onClick={closeCallback}>
          <CloseIcon />
        </CloseButton>
      </Header>
      <Body>
        <BodyTitle>Daily Tasks</BodyTitle>
        {programLoop}
        {programs.length === 0 && <div>{name} did not have any activity with RESTORE on this day</div>}
      </Body>

      {showWarning && (
        <Warning>
          <div>
            <WarningIcon />
          </div>

          <div>
            Please note that combining Active ROM and Passive ROM Programs, does not count as an active day for billing
            purposes
          </div>
        </Warning>
      )}
    </Container>
  );
};

export default RestorativeActivitySummary;
